import { FormikProps, FormikProvider } from 'formik';
import { FC, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import useDeclarations from 'hooks/useDeclarations';
import useProducts from 'hooks/useProducts';
import useDeclarationFooter from 'hooks/useDeclarationFooter';
import useProductNavigation from 'hooks/useProductNavigation';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { FormTabs } from './DeclarationFormTab';
import { ALL_ITEMS_PAGE_SIZE, PaginatedParams } from 'core/http/pagination';
import { ICS2ConsignmentHouseLevel, ICS2GoodsItemCategory } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { getFormActiveTabKey } from './utils/getFormActiveTabKey';
import useHouseGoodsItems from './utils/useHouseGoodsItems';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import useDeclarationValidation from 'hooks/useDeclarationValidation';
import useDeclarationProducts from 'hooks/useDeclarationProducts';
import useGetDeclarationMapValues from 'hooks/useGetDeclarationMapValues';
import { getDeclarationType } from 'views/declarations/utils/declaration-utils';
import useCodelists from 'hooks/useCodelists';
import { DeclarationFooterType } from 'store/declaration-footer/declaration-footer';
import { NctsDeclaration } from 'store/declarations/ireland/ncts-declaration';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import FormHelperDrawer from 'views/declarations/tooltips-help/FormHelpDrawer';
import { StyledHeader } from './Form.styles';
import { Box44Context } from 'views/declarations/common/box44/Box44';
import DeclarationFormContent from './DeclarationFormContent';
import DeclarationsProductsTemplates from 'views/declarations/common/DeclarationsProductsTemplates';
import Drawer from 'components/ui/base/drawer/Drawer';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { Declaration } from 'store/declarations/declaration';
import { useProductContext } from './context/ProductContext';
import useFormUtils from 'hooks/useFormUtils';
import { ProductsTypes } from 'store/products/reducer';
import { ListPayload } from 'core/http/response';
import { box44PathAndFieldNames } from 'views/declarations/common/box44/mappers';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import FormNavigation from './components/FormNavigation';
import { CodelistsLoadingContext } from 'components/ui/composed/declarations/formSelect/CodelistsLoadingContext';

interface FormHelp {
    visible: boolean;
    section: number | string | undefined;
}

interface Props {
    formik: FormikProps<any>;
    productsFormik: FormikProps<ProductsTypes>;
    houseConsignmentFormik?: FormikProps<any>;
    declaration: Declaration;
    products: ListPayload<ProductsTypes>;
    cancelProducts: boolean;
    clearCancel: Function;
    saveOpenProductIfNeeded?: () => Promise<boolean>;
    revalidateItems?: () => Promise<void>;
    onActiveProductParamsChange?: (params: Partial<PaginatedParams> | undefined) => void;
}

const DeclarationFormTabLayout: FC<Props> = ({
    formik,
    productsFormik,
    houseConsignmentFormik,
    declaration,
    cancelProducts,
    clearCancel,
    products,
    saveOpenProductIfNeeded,
    revalidateItems,
    onActiveProductParamsChange,
}) => {
    const { setDeclarationFooterType } = useDeclarationFooter();
    const { declarationTemplate, createEnsIcs2HouseConsignment } = useDeclarations();
    const { declarationId, manifestId, jobId, productId } = useParams();
    const location = useLocation();
    const { formType, internalType, countryLowerCase: country } = useFormUtils();
    const { transformData } = useGetDeclarationMapValues();
    const declarationType = getDeclarationType(declaration);
    const { getCodeLists, checkLoading } = useCodelists();
    const customerId = declaration.individual?.customerId;

    const codelistsLoading = checkLoading(country);
    const baseUrl = getDeclarationUrl({ manifestId, jobId, declarationId });
    const inProducts = location.pathname.includes('products');

    // The master form is the source the house consignments list renders from, so the navigation
    // counts and indices are taken from there as well rather than from the stored declaration.
    const houseConsignments: ICS2ConsignmentHouseLevel[] =
        formik?.values?.consignmentMasterLevel?.consignmentHouseLevel ?? [];

    const { houseGoodsItems, refetchHouseGoodsItems } = useHouseGoodsItems(declarationId, declaration);

    const [selectedFormTab, setSelectedFormTab] = useState<FormTabs>(getFormActiveTabKey(location, baseUrl));

    const [isDeclarationProductTemplatesDrawerVisible, setDeclarationProductTemplatesDrawerVisible] =
        useState<boolean>(false);

    const { activeProductParams, updateActiveProductParams, refetchProducts, fetchProductPage } =
        useProductNavigation(onActiveProductParamsChange);

    const [formHelpDrawer, setFormHelpDrawer] = useReducer(
        (initialState: FormHelp, newState: Partial<FormHelp>) => ({ ...initialState, ...newState }),
        { visible: false, section: undefined }
    );

    const { listDeclarationProducts, createDeclarationProduct, getDeclarationProduct } = useProducts();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { addProductTemplatesToDeclaration } = useDeclarationProducts();
    const { declarationValidation, setFormAction } = useDeclarationValidation();
    const controlProduct = useProductContext();
    const navigate = useNavigate();

    const { saveAsDraft, amendment } = useOutletContext<{ saveAsDraft: Function; amendment: boolean }>() ?? {};

    useEffect(() => {
        setSelectedFormTab(getFormActiveTabKey(location, baseUrl));
    }, [location, declarationId, baseUrl]);

    useEffect(() => {
        getCodeLists(country, declarationType, customerId);
    }, [getCodeLists, country, declarationType, customerId]);

    useEffect(() => {
        setDeclarationFooterType(DeclarationFooterType.MASTER_DETAILS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (cancelProducts) {
            productsFormik.resetForm();
            setDeclarationFooterType(DeclarationFooterType.MASTER_DETAILS);
            clearCancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cancelProducts]);

    useEffect(() => {
        if (selectedFormTab === FormTabs.MASTER_DETAILS) {
            if (!declarationValidation.formAction) {
                setFormAction(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFormTab]);

    const handleCreateDeclarationProduct = useCallback(async () => {
        const productSaved = (await saveOpenProductIfNeeded?.()) ?? true;
        if (!productSaved) return;

        controlProduct.setProductId?.(undefined);
        productsFormik?.resetForm();
        setDeclarationFooterType(DeclarationFooterType.PRODUCTS);
        if (!declarationId) return;

        let form: Record<string, string> = {};
        if (declaration?.ieNctsDeclaration || declaration?.ukNctsDeclaration)
            form.houseConsignmentId = (formik?.values as NctsDeclaration)?.consignment?.houseConsignment?.[0]?.id;

        const product = await createDeclarationProduct({
            templateData: declarationTemplate?.template?.product.defaults ?? form,
            declarationId,
            formType,
            internalType,
            country,
        });
        await listDeclarationProducts({ declarationId, formType, internalType, country });
        navigate(`${baseUrl}/products/${product.id}`);
    }, [
        controlProduct,
        saveOpenProductIfNeeded,
        productsFormik,
        setDeclarationFooterType,
        declarationId,
        createDeclarationProduct,
        declarationTemplate?.template?.product.defaults,
        listDeclarationProducts,
        navigate,
        formType,
        internalType,
        country,
        declaration?.ieNctsDeclaration,
        declaration?.ukNctsDeclaration,
        formik?.values,
        baseUrl,
    ]);

    // The empty item is created straight away, the same way declaration products are, so that it has
    // an id and a place in the list - and with it a working item navigation - from the first render
    // of the form.
    const handleCreateMasterItemProduct = useCallback(async () => {
        const productSaved = (await saveOpenProductIfNeeded?.()) ?? true;
        if (!productSaved) return;

        controlProduct.setProductId?.(undefined);
        productsFormik?.resetForm();
        setDeclarationFooterType(DeclarationFooterType.PRODUCTS);
        if (!declarationId) return;

        const product = await createDeclarationProduct({
            templateData: { category: ICS2GoodsItemCategory.MASTER },
            declarationId,
            formType,
            internalType,
            country,
        });
        if (!product?.id) return;

        await listDeclarationProducts({
            declarationId,
            formType,
            internalType,
            country,
            params: { size: ALL_ITEMS_PAGE_SIZE, category: ICS2GoodsItemCategory.MASTER },
        });

        navigate(`${baseUrl}/master-items/${product.id}`);
    }, [
        controlProduct,
        saveOpenProductIfNeeded,
        productsFormik,
        setDeclarationFooterType,
        declarationId,
        createDeclarationProduct,
        listDeclarationProducts,
        navigate,
        formType,
        internalType,
        country,
        baseUrl,
    ]);

    // House consignments have no endpoint of their own - an empty one is appended to the declaration
    // and saved, and the backend hands back the id to open it with.
    const handleCreateHouseConsignment = useCallback(async () => {
        if (!(declarationId && declaration)) return;

        // The overlay blocks input for as long as the save runs: a second click would build its own
        // request from the declaration this one is about to replace, and one of the two would win.
        showGlobalOverlay({ type: 'LoadingOverlay' });

        try {
            const updatedDeclaration = await createEnsIcs2HouseConsignment(declarationId, declaration);

            // The new entry is appended to the list, so it is the last one the backend sends back.
            const houseConsignments =
                updatedDeclaration?.ics2ENSDeclaration?.payload?.consignmentMasterLevel?.consignmentHouseLevel ?? [];
            const created = houseConsignments[houseConsignments.length - 1];

            if (!created?.id) return;

            navigate(`${baseUrl}/house-consignments/${created.id}`);
        } catch (error) {
            Notification({
                type: 'error',
                messageTitle: 'Adding the house consignment failed.',
                description: getErrorMessage(error) || 'Something went wrong.',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    }, [
        baseUrl,
        createEnsIcs2HouseConsignment,
        declaration,
        declarationId,
        hideGlobalOverlay,
        navigate,
        showGlobalOverlay,
    ]);

    // House level items are created through the products endpoint like any other item, addressed by
    // the consignment they belong to.
    const handleCreateHouseConsignmentProduct = useCallback(
        async (houseConsignmentId: string) => {
            const productSaved = (await saveOpenProductIfNeeded?.()) ?? true;
            if (!productSaved) return;

            controlProduct.setProductId?.(undefined);
            productsFormik?.resetForm();
            setDeclarationFooterType(DeclarationFooterType.PRODUCTS);
            if (!(declarationId && houseConsignmentId)) return;

            const product = await createDeclarationProduct({
                templateData: { category: ICS2GoodsItemCategory.HOUSE, houseConsignmentId },
                declarationId,
                formType,
                internalType,
                country,
            });
            if (!product?.id) return;

            await refetchHouseGoodsItems();

            navigate(`${baseUrl}/house-consignments/${houseConsignmentId}/${product.id}`);
        },
        [
            controlProduct,
            saveOpenProductIfNeeded,
            productsFormik,
            setDeclarationFooterType,
            declarationId,
            createDeclarationProduct,
            refetchHouseGoodsItems,
            navigate,
            formType,
            internalType,
            country,
            baseUrl,
        ]
    );

    const handleAddProductTemplatesToDeclaration = async (productTemplateIds: string[]) => {
        setDeclarationProductTemplatesDrawerVisible(false);
        if (!(declaration?.id && declarationId)) return;

        await addProductTemplatesToDeclaration({
            declarationId: declaration?.id,
            productTemplateIds,
            formType,
            internalType,
            country,
        });

        await listDeclarationProducts({ declarationId, formType, internalType, country });
        navigate(`${baseUrl}/products`);
    };

    const handleToggleHelp = useCallback((refNumber: number | string) => {
        setFormHelpDrawer({ visible: true, section: refNumber });
    }, []);

    const handleEditDeclarationProduct = useCallback(
        async (productId: string) => {
            if (!declaration?.id) return;

            const response = await getDeclarationProduct({
                declarationId: declaration.id,
                productId,
                formType,
                internalType,
                country,
            });

            if (!response) return;

            controlProduct.setProductId?.(response.id);

            if (transformData?.product?.forClient) {
                let transformedProduct = transformData.product.forClient(response);

                if (country === DeclarationCountry.UK) {
                    transformedProduct = transformedProduct.governmentAgencyGoodsItem;
                }

                productsFormik?.setValues(transformedProduct);
            }

            navigate(`${baseUrl}/products/${response.id}`);
        },
        [
            declaration?.id,
            getDeclarationProduct,
            formType,
            internalType,
            country,
            controlProduct,
            transformData,
            productsFormik,
            navigate,
            baseUrl,
        ]
    );

    const outletContext = useMemo(
        () => ({
            formik,
            productsFormik,
            houseConsignmentFormik,
            toggleHelp: handleToggleHelp,
            onEdit: handleEditDeclarationProduct,
            saveAsDraft,
            amendment,
            setActiveProductParams: updateActiveProductParams,
            houseGoodsItems,
            refetchHouseGoodsItems,
            onCreateHouseConsignmentProduct: handleCreateHouseConsignmentProduct,
            revalidateItems,
        }),
        [
            formik,
            productsFormik,
            houseConsignmentFormik,
            handleToggleHelp,
            handleEditDeclarationProduct,
            saveAsDraft,
            amendment,
            updateActiveProductParams,
            houseGoodsItems,
            refetchHouseGoodsItems,
            handleCreateHouseConsignmentProduct,
            revalidateItems,
        ]
    );

    const box44Path = useMemo(() => {
        const paths = box44PathAndFieldNames[formType as MessageTypes];
        if (location.pathname.includes('house-consignments')) return paths?.houseConsignmentPath;
        if (location.pathname.includes('products')) return paths?.productPath;
        return paths?.masterPath;
    }, [location.pathname, formType]);

    // Note: The additional fields of box 44 items are present only on the item level and only for Irish import H1-H7
    const hasAdditionalInformationFields =
        inProducts &&
        declaration.declarationInternalType === DeclarationInternalType.IMPORT &&
        country === DeclarationCountry.IRELAND;

    const currentFormik = useMemo(() => {
        // Every item form - declaration products, ICS2 master items, house consignment items - is
        // edited with the products formik. FormNavigation reads its dirty state to decide whether
        // the item has to be saved before stepping to another one.
        if (productId) return productsFormik;
        if (location.pathname.includes('house-consignments')) return houseConsignmentFormik;
        if (location.pathname.includes('products')) return productsFormik;
        return formik;
    }, [formik, houseConsignmentFormik, location.pathname, productId, productsFormik]);

    if (!declaration || !currentFormik) return null;

    return (
        <CodelistsLoadingContext.Provider value={codelistsLoading}>
            <FormHelperDrawer
                section={formHelpDrawer.section}
                isVisible={formHelpDrawer.visible}
                onClose={() => setFormHelpDrawer({ visible: false })}
            />
            <StyledHeader>
                <FormikProvider value={currentFormik}>
                    <Box44Context.Provider
                        value={{
                            // Dynamic key names
                            documentTypeName: box44PathAndFieldNames[formType as MessageTypes]?.documentTypeName,
                            documentIdentifierName:
                                box44PathAndFieldNames[formType as MessageTypes]?.documentIdentifierName,
                            monetaryAmountName: box44PathAndFieldNames[formType as MessageTypes]?.monetaryAmountName,
                            measurementName: box44PathAndFieldNames[formType as MessageTypes]?.measurementName,
                            // Other props
                            path: box44Path,
                            hasAdditionalInfo: hasAdditionalInformationFields,
                            messageType: formType as MessageTypes,
                            isProduct: inProducts,
                        }}
                    >
                        <FormNavigation
                            products={products}
                            houseConsignments={houseConsignments}
                            houseGoodsItems={houseGoodsItems}
                            numItems={products?.total || 0}
                            selectedFormTab={selectedFormTab}
                            setShowProducts={setDeclarationProductTemplatesDrawerVisible}
                            handleCreateDeclarationProduct={handleCreateDeclarationProduct}
                            handleCreateMasterItemProduct={handleCreateMasterItemProduct}
                            handleCreateHouseConsignment={handleCreateHouseConsignment}
                            handleCreateHouseConsignmentProduct={handleCreateHouseConsignmentProduct}
                            // For now we have bulk upload items only for Irish Import H1
                            showBulkUploadItems={Boolean(declaration.irelandImportDeclaration)}
                            refetchProducts={refetchProducts}
                            fetchProductPage={fetchProductPage}
                            activeProductParams={activeProductParams}
                            saveOpenProductIfNeeded={saveOpenProductIfNeeded}
                        />
                    </Box44Context.Provider>
                </FormikProvider>
            </StyledHeader>

            <DeclarationFormContent>
                <Outlet context={outletContext} />
            </DeclarationFormContent>

            <Drawer
                title={`View all ${formType} product templates`}
                width="786"
                open={isDeclarationProductTemplatesDrawerVisible}
                onClose={() => {
                    setDeclarationProductTemplatesDrawerVisible(false);
                }}
            >
                {isDeclarationProductTemplatesDrawerVisible && (
                    <DeclarationsProductsTemplates
                        addProductsToDeclaration={handleAddProductTemplatesToDeclaration}
                        declarationTemplate={declarationTemplate}
                    />
                )}
            </Drawer>
        </CodelistsLoadingContext.Provider>
    );
};
export default DeclarationFormTabLayout;
